import {inject, Injectable} from '@angular/core';
import {SecureSessionStorageService} from '../secure-session-storage/secure-session-storage.service';

/**
 * SessionStorage-basierte Zwischenspeicherung der Eingaben des Ticket-Überprüfungs-Wizards.
 * Die Daten werden pro Ticket unter einem eigenen Key abgelegt und beim Abschluss/Abbruch
 * des Wizards wieder entfernt. SessionStorage wird bewusst statt localStorage verwendet,
 * damit die Daten spätestens beim Schließen des Tabs automatisch verworfen werden.
 *
 * Die Step-Daten enthalten personenbezogene Daten (Name, Adresse, Zahlungsdaten, ...) und werden
 * daher nicht direkt, sondern verschlüsselt über SecureSessionStorageService abgelegt - dieser
 * Service hält selbst keine Klartext-Kopie, sondern reicht nur an dessen (Promise-basierte)
 * set()/get()-Methoden durch. Aufrufer, die synchronen Zugriff brauchen (z.B. Template-Bindings),
 * müssen ihren eigenen Zwischenstand halten und ihn beim Verlassen eines Schritts hier speichern.
 */
@Injectable({
  providedIn: 'root'
})
export class VerificationWizardStorageService {

  private readonly secureStorage = inject(SecureSessionStorageService);

  private keyFor(ticketId: string): string {
    return `sfk-verification-wizard-${ticketId}`;
  }

  async saveStepData(ticketId: string, stepId: string, data: unknown): Promise<void> {
    const all = await this.getAllStepData(ticketId);
    all[stepId] = data;
    await this.secureStorage.set(this.keyFor(ticketId), all);
  }

  async getStepData<T>(ticketId: string, stepId: string): Promise<T | null> {
    const all = await this.getAllStepData(ticketId);
    return (all[stepId] as T) ?? null;
  }

  async getAllStepData(ticketId: string): Promise<Record<string, unknown>> {
    return (await this.secureStorage.get<Record<string, unknown>>(this.keyFor(ticketId))) ?? {};
  }

  clearAll(ticketId: string): void {
    this.secureStorage.remove(this.keyFor(ticketId));
  }
}
